import { pathToFeatureId } from './pathToFeatureId';

/**
 * When false/unset, no HTTP calls are made (avoids Vite proxy ECONNREFUSED if the gateway on :4030 is not running).
 * Set VITE_OBSERVABILITY_TELEMETRY=true and run `npm run dev:observability`, or set VITE_OBSERVABILITY_GATEWAY_URL.
 */
function isTelemetryEnabled(): boolean {
  if (import.meta.env.VITE_OBSERVABILITY_TELEMETRY === 'false') return false;
  const direct = (import.meta.env.VITE_OBSERVABILITY_GATEWAY_URL as string | undefined)?.trim();
  if (direct) return true;
  return import.meta.env.VITE_OBSERVABILITY_TELEMETRY === 'true';
}

function baseUrl(): string {
  const b = import.meta.env.VITE_OBSERVABILITY_GATEWAY_URL as string | undefined;
  if (b && b.length > 0) return b.replace(/\/$/, '');
  return '/api/observability';
}

function headers(): HeadersInit {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const t = import.meta.env.VITE_OBSERVABILITY_INGEST_TOKEN as string | undefined;
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

export async function ingestMetric(payload: {
  featureId: string;
  latencyMs?: number;
  errorRate?: number;
  throughput?: number;
  usage?: number;
  region?: string;
}): Promise<void> {
  if (!isTelemetryEnabled()) return;
  try {
    await fetch(`${baseUrl()}/v1/ingest/metrics`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    /* non-blocking — never break UX */
  }
}

export async function ingestLog(entry: {
  level: string;
  message: string;
  featureId?: string;
  traceId?: string;
}): Promise<void> {
  try {
    await fetch(`${baseUrl()}/v1/ingest/logs`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(entry),
      keepalive: true,
    });
  } catch {
    /* ignore */
  }
}

export async function ingestEvent(entry: {
  type: string;
  featureId?: string;
  payload?: Record<string, unknown>;
}): Promise<void> {
  if (!isTelemetryEnabled()) return;
  try {
    await fetch(`${baseUrl()}/v1/ingest/events`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(entry),
      keepalive: true,
    });
  } catch {
    /* ignore */
  }
}

export async function ingestBatch(
  items: Array<Record<string, unknown>>
): Promise<void> {
  if (!isTelemetryEnabled()) return;
  try {
    await fetch(`${baseUrl()}/v1/ingest/batch`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ items }),
    });
  } catch {
    /* ignore */
  }
}

/** Derive feature id from current browser path */
export function currentFeatureIdFromPath(pathname: string): string {
  return pathToFeatureId(pathname);
}
